import React, { useMemo } from 'react';
import {
    KPICard, SplitBar, EmptyState, SectionTitle,
    DEVICE_COLORS, SOURCE_COLORS, CHART_PALETTE,
    formatDuration, formatNumber, formatPercent,
} from './analyticsHelpers';

// ─── Duration histogram ───────────────────────────────────────────────────────
// Buckets: 0-15s, 15-30s, 30s-1m, 1-2m, 2-5m, 5-10m, 10m+
const DURATION_BUCKETS = [
    { label: '<15s',   min: 0,    max: 15  },
    { label: '15–30s', min: 15,   max: 30  },
    { label: '30–60s', min: 30,   max: 60  },
    { label: '1–2m',   min: 60,   max: 120 },
    { label: '2–5m',   min: 120,  max: 300 },
    { label: '5–10m',  min: 300,  max: 600 },
    { label: '10m+',   min: 600,  max: Infinity },
];

const DurationHistogram = ({ kpis }) => {
    // We don't have raw sessions — reconstruct distribution from avgDuration heuristic.
    // The real fix is to expose session durations from the backend. For now we build
    // a plausible approximate distribution centred on avgDuration.
    const avgSec = kpis.avgDurationSeconds || 0;
    const total = kpis.totalSessions || 0;

    // Estimate distribution using a log-normal centred on avg.
    // This is clearly labelled as estimated until backend exposes raw durations.
    const counts = useMemo(() => {
        if (total === 0 || avgSec === 0) return DURATION_BUCKETS.map(() => 0);

        const logMean = Math.log(Math.max(avgSec, 1));
        const sigma = 0.9; // log-normal spread

        const bucketProbs = DURATION_BUCKETS.map(b => {
            const lo = b.min === 0 ? 0 : Math.log(b.min);
            const hi = b.max === Infinity ? Infinity : Math.log(b.max);
            // Area under log-normal between lo and hi (approximated by midpoint density × width)
            const mid = b.max === Infinity ? logMean + 2 * sigma : (lo + hi) / 2;
            const density = Math.exp(-0.5 * ((mid - logMean) / sigma) ** 2) / (sigma * Math.sqrt(2 * Math.PI));
            const width = b.max === Infinity ? 2 : Math.log(b.max) - Math.log(Math.max(b.min, 1));
            return Math.max(0, density * width);
        });

        const probSum = bucketProbs.reduce((a, b) => a + b, 0);
        return bucketProbs.map(p => Math.round((p / probSum) * total));
    }, [avgSec, total]);

    const max = Math.max(...counts, 1);
    const bounceCount = counts[0] + counts[1]; // <30s
    const bounceRate = total > 0 ? Math.round((bounceCount / total) * 100) : 0;

    return (
        <div>
            <div className="histogram">
                {DURATION_BUCKETS.map((b, i) => {
                    const isBounceBucket = i < 2;
                    return (
                        <div key={b.label} className="histo-col">
                            <span className="histo-count">{counts[i]}</span>
                            <div className="histo-bar-wrap">
                                <div
                                    className="histo-bar"
                                    style={{
                                        height: `${(counts[i] / max) * 100}%`,
                                        background: isBounceBucket ? '#ef4444' : '#2B8EE8',
                                        opacity: isBounceBucket ? 0.7 : 0.6,
                                    }}
                                    title={`${b.label}: ~${counts[i]} sessions`}
                                />
                            </div>
                            <span className="histo-label">{b.label}</span>
                        </div>
                    );
                })}
            </div>
            <div className="histo-annotation">
                <span className="bounce-pill">
                    ~{bounceRate}% bounce rate
                    <span className="bounce-pill-sub"> (sessions &lt;30s)</span>
                </span>
                <span className="histo-note">Distribution estimated from avg session duration</span>
            </div>
        </div>
    );
};

// ─── Device donut (SVG, no deps) ─────────────────────────────────────────────
const DeviceDonut = ({ devices }) => {
    const total = Object.values(devices || {}).reduce((a, b) => a + b, 0);
    if (total === 0) return <EmptyState message="No session data" />;

    const entries = Object.entries(devices).sort((a, b) => b[1] - a[1]);
    const colors = DEVICE_COLORS;
    const r = 50;
    const cx = 70;
    const cy = 70;
    const strokeWidth = 16;

    let offset = 0;
    const circumference = 2 * Math.PI * r;
    const segments = entries.map(([key, val]) => {
        const pct = val / total;
        const dash = pct * circumference;
        const seg = { key, val, pct, dash, offset, color: colors[key] || '#94A3B8' };
        offset += dash;
        return seg;
    });

    return (
        <div className="donut-wrap">
            <svg width={140} height={140} viewBox="0 0 140 140">
                {/* Background ring */}
                <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={strokeWidth} />
                {segments.map(seg => (
                    <circle
                        key={seg.key}
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill="none"
                        stroke={seg.color}
                        strokeWidth={strokeWidth}
                        strokeDasharray={`${seg.dash} ${circumference - seg.dash}`}
                        strokeDashoffset={circumference / 4 - seg.offset}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 0.4s ease' }}
                    />
                ))}
                <text x={cx} y={cy - 6} textAnchor="middle" fontSize="20" fontWeight="800" fontFamily="Syne" fill="var(--text)">
                    {total}
                </text>
                <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9" fill="var(--text-3)" fontFamily="DM Sans">
                    SESSIONS
                </text>
            </svg>
            <div className="donut-legend">
                {segments.map(seg => (
                    <div key={seg.key} className="donut-legend-item">
                        <span className="donut-dot" style={{ background: seg.color }} />
                        <span className="donut-label">{seg.key}</span>
                        <span className="donut-value">{seg.val}</span>
                        <span className="donut-pct">{Math.round(seg.pct * 100)}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Entry funnel ─────────────────────────────────────────────────────────────
const EntryFunnel = ({ entrySource, gpsConsentRate, totalSessions }) => {
    const sourceEntries = Object.entries(entrySource || {});
    const gpsCount = Math.round((gpsConsentRate / 100) * totalSessions);

    const steps = [
        {
            label: 'Map opened',
            count: totalSessions,
            pct: 100,
            color: '#2B8EE8',
        },
        ...sourceEntries.map(([key, val], i) => ({
            label: `via ${key}`,
            count: val,
            pct: totalSessions > 0 ? Math.round((val / totalSessions) * 100) : 0,
            color: SOURCE_COLORS[key] || CHART_PALETTE[i],
        })),
        {
            label: 'GPS granted',
            count: gpsCount,
            pct: gpsConsentRate,
            color: '#10b981',
            divider: true,
        },
    ];

    const maxCount = Math.max(...steps.map(s => s.count), 1);

    return (
        <div className="funnel-wrap">
            {steps.map((step, i) => (
                <React.Fragment key={step.label}>
                    {step.divider && <div className="funnel-divider" />}
                    <div className="funnel-step">
                        <div className="funnel-step-meta">
                            <span className="funnel-step-label">{step.label}</span>
                            <span className="funnel-step-count">
                                {step.count} <span className="funnel-step-pct">({step.pct}%)</span>
                            </span>
                        </div>
                        <div className="funnel-bar-bg">
                            <div
                                className="funnel-bar-fill"
                                style={{
                                    width: `${(step.count / maxCount) * 100}%`,
                                    background: step.color,
                                }}
                            />
                        </div>
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
};

// ─── Sessions over time (using hourly data as proxy) ─────────────────────────
const SessionsTimeline = ({ hourlyActivity }) => {
    if (!hourlyActivity) return <EmptyState message="No data" />;
    const data = hourlyActivity;
    const max = Math.max(...data, 1);
    const total = data.reduce((a, b) => a + b, 0);
    const peakH = data.indexOf(Math.max(...data));

    return (
        <div>
            <div className="sessions-timeline">
                {data.map((count, h) => (
                    <div
                        key={h}
                        className="timeline-col"
                        title={`${h}:00 — ${count} sessions`}
                    >
                        <div className="timeline-bar-wrap">
                            <div
                                className="timeline-bar"
                                style={{
                                    height: `${(count / max) * 100}%`,
                                    background: h === peakH ? '#f59e0b' : '#2B8EE8',
                                    opacity: count === 0 ? 0 : h === peakH ? 1 : 0.55,
                                }}
                            />
                        </div>
                        {h % 6 === 0 && (
                            <div className="timeline-label">{h}h</div>
                        )}
                    </div>
                ))}
            </div>
            {max > 0 && (
                <p className="chart-annotation">
                    {total} total sessions · Peak at {peakH}:00 ({data[peakH]} sessions)
                </p>
            )}
        </div>
    );
};

// ─── SessionsTab ──────────────────────────────────────────────────────────────
const SessionsTab = ({ analyticsData }) => {
    const { kpis } = analyticsData;

    return (
        <div className="ad-tab-content">

            {/* ── KPI strip ── */}
            <section className="ad-section">
                <div className="kpi-grid">
                    <KPICard
                        label="Total Sessions"
                        value={formatNumber(kpis.totalSessions)}
                        sub="all visitors"
                        accent="#2B8EE8"
                    />
                    <KPICard
                        label="Avg Duration"
                        value={formatDuration(kpis.avgDurationSeconds)}
                        sub="per session"
                        accent="#10b981"
                    />
                    <KPICard
                        label="GPS Consent"
                        value={formatPercent(kpis.gpsConsentRate)}
                        sub="location enabled"
                        accent="#06b6d4"
                    />
                    <KPICard
                        label="Peak Concurrent"
                        value={kpis.peakConcurrentSessions || 0}
                        sub="at same time"
                        accent="#f59e0b"
                    />
                    <KPICard
                        label="Mobile Share"
                        value={formatPercent(
                            kpis.totalSessions > 0
                                ? Math.round(((kpis.devices?.mobile || 0) / kpis.totalSessions) * 100)
                                : 0
                        )}
                        sub="of all sessions"
                        accent="#ec4899"
                    />
                    <KPICard
                        label="QR Entry"
                        value={formatPercent(
                            kpis.totalSessions > 0
                                ? Math.round(((kpis.entrySource?.qr || 0) / kpis.totalSessions) * 100)
                                : 0
                        )}
                        sub="via QR code"
                        accent="#8b5cf6"
                    />
                </div>
            </section>

            {/* ── Duration histogram + device donut ── */}
            <section className="ad-section ad-two-col">
                <div className="ad-card ad-card-wide">
                    <SectionTitle sub="How long attendees stayed on the map">
                        Session Duration Distribution
                    </SectionTitle>
                    <DurationHistogram kpis={kpis} />
                </div>
                <div className="ad-card">
                    <SectionTitle sub="Breakdown by device type">Device Split</SectionTitle>
                    <DeviceDonut devices={kpis.devices} />
                </div>
            </section>

            {/* ── Entry funnel + sessions over time ── */}
            <section className="ad-section ad-two-col">
                <div className="ad-card">
                    <SectionTitle sub="How attendees reached and engaged with the map">
                        Engagement Funnel
                    </SectionTitle>
                    <EntryFunnel
                        entrySource={kpis.entrySource}
                        gpsConsentRate={kpis.gpsConsentRate}
                        totalSessions={kpis.totalSessions}
                    />
                </div>
                <div className="ad-card ad-card-wide">
                    <SectionTitle sub="Sessions started per hour">Sessions Over Time</SectionTitle>
                    <SessionsTimeline hourlyActivity={kpis.hourlyActivity} />
                </div>
            </section>

        </div>
    );
};

export default SessionsTab;